import React from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  IMG_ORGANIC_COTTON_IMAGE as OrganicCottonImage,
  IMG_SPULE as SpuleIcon,
  IMG_ICON_SHIPPING as ShippingIcon,
  IMG_BAUMWOLLE_ICON as BaumwolleIcon,
  IMG_OUR_MISSION_IMAGE as OurMissionImage,
} from "./imageConfig";
import GotsIconSvg from "./images/GotsIconSvg";
import Resources from "./Resources";

const Sustainability = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  
  const features = [
    {
      icon: BaumwolleIcon,
      text: "ORGANIC\nCOTTON",
    },
    {
      icon: ShippingIcon,
      text: "CLIMATE\nNEUTRAL\nSHIPPING",
    },
    {
      icon: SpuleIcon,
      text: "MADE\nIN\nINDIA",
    },
    {
      icon: <GotsIconSvg />,
      text: "GOTS\nCERTIFIED",
    },
  ];

  const FeatureRow = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: isMobile ? "center" : "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: isMobile ? 2 : 4,
          px: isMobile ? 1 : 2,
          py: isMobile ? 2 : 4,
        }}
      >
        {features.map(({ icon, text }, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 1 : 2,
              minWidth: isMobile ? "40%" : 180,
              mb: isMobile ? 2 : 0,
            }}
          >
            {/* Icon */}
            {typeof icon === "string" ? (
              <Box
                component="img"
                src={icon}
                alt=""
                sx={{ 
                  width: isMobile ? 50 : 70, 
                  height: isMobile ? 50 : 70, 
                  objectFit: "contain" 
                }}
              />
            ) : (
              <Box sx={{ width: isMobile ? 50 : 70 }}>{icon}</Box>
            )}

            {/* Text */}
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? "14px" : "20px",
                whiteSpace: "pre-line",
                fontFamily: "inherit",
                ml: isMobile ? "2px" : "0px",
                fontWeight: 400,
              }}
            >
              {text === "GOTS\nCERTIFIED" && isMobile ? "" : text}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        p: isMobile ? 1 : 2,
        mt: isMobile ? 20 : 34,
      }}
    >
      {/* Sustainability Text - Top Right Corner of Page */}
      <Typography
        variant="h2"
        sx={{
          position: "absolute",
          top: isMobile ? 120 : isTablet ? 180 : 180,
          left: isMobile ? 14 : isTablet ? -90 : 0,
          textTransform: "none",
          fontSize: isMobile ? "2.5rem" : isTablet ? "3.5rem" : "5rem",
          color: "#000",
          zIndex: 2,
          pl: isMobile ? 0 : 14,
        }}
      >
        Sustainability
      </Typography>

      {/* Image Container */}
      <Box
        sx={{
          position: "relative",
          display: "inline-block",
          overflow: "hidden",
          lineHeight: 0,
        }}
      >
        <Box
          component="img"
          src={OrganicCottonImage}
          alt="Organic Cotton"
          sx={{
            display: "block",
            width: isMobile ? "95%" : "60%",
            height: "auto",
            p: 1
          }}
        />

        {/* Top-Right Text: DAINA */}
        <Typography
          sx={{
            position: "absolute",
            top: isMobile ? "40%" : 300,
            right: isMobile ? 16 : 16,
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontSize: isMobile ? "2rem" : isTablet ? "3.5rem" : "5rem",
            fontWeight: 400,
            letterSpacing: "0.1em",
            color: "#000",
            zIndex: 2,
            textAlign: "right",
          }}
        >
          DAINA
          <Typography
            sx={{
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: isMobile ? "2rem" : isTablet ? "3.5rem" : "5rem",
              fontWeight: 400,
              letterSpacing: "0.1em",
              color: "#000",
              zIndex: 2,
              mt: isMobile ? -2 : -4,
              textAlign: "right",
            }}
          >
            in Focus
          </Typography>
        </Typography>
      </Box>

      {/* Sustainability Message and Icons */}
      <Box sx={{ 
        maxWidth: 1000, 
        textAlign: "center", 
        mt: isMobile ? 4 : 8, 
        px: isMobile ? 1 : 2 
      }}>
        <Typography variant="h3" sx={{ 
          fontSize: isMobile ? "1.2rem" : isTablet ? "1.5rem" : "2rem", 
          mb: isMobile ? 1 : 2 
        }}>
          At DAINA, we pay attention to sustainable material selection and a
          transparent supply chain.
        </Typography>
        <Typography variant="h3" sx={{ 
          fontSize: isMobile ? "1.2rem" : isTablet ? "1.5rem" : "2rem", 
          mb: isMobile ? 2 : 4, 
          mt: isMobile ? 3 : 6 
        }}>
          Our timeless classics and limited editions are produced worldwide by
          trusted, certified partners, using innovative techniques designed to
          minimise environmental impact.
        </Typography>
        <FeatureRow />
        
        {/* Our VALUES Section */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: isMobile ? "flex-start" : "flex-end",
            overflow: "hidden",
            lineHeight: 0,
            width: "100%",
            mt: isMobile ? 12 : 24,
          }}
        >
          <Box
            component="img"
            src={OrganicCottonImage}
            alt="Organic Cotton"
            sx={{
              display: "block",
              width: isMobile ? "100%" : "60%",
              height: "auto",
            }}
          />

          {/* "Our VALUES" Text */}
          <Typography
            variant="h2"
            sx={{
              position: "absolute",
              top: isMobile ? 10 : -10,
              left: isMobile ? 16 : 40,
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: isMobile ? "2.2rem" : isTablet ? "3.5rem" : "5rem",
              fontWeight: 400,
              letterSpacing: "0.1em",
              color: "#000",
              zIndex: 2,
              textAlign: "left",
            }}
          >
            Our
            <br />
            VALUES
          </Typography>

          {/* Values List */}
          <Box
            sx={{
              position: "absolute",
              bottom: isMobile ? 20 : 40,
              left: isMobile ? 8 : 10,
              zIndex: 2,
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontSize: isMobile ? "1.5rem" : isTablet ? "2rem" : "2.5rem",
                fontWeight: 400,
                color: "#000",
                lineHeight: 1.3,
                textAlign: "right",
              }}
            >
              Quality
              <br />
              Respect
              <br />
              Responsibility
              <br />
              Appreciation
            </Typography>
          </Box>
        </Box>

        {/* Our MISSION Section */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            overflow: "hidden",
            lineHeight: 0,
            width: "100%",
            mt: isMobile ? 12 : 24,
          }}
        >
          {/* Image */}
          <Box
            component="img"
            src={OurMissionImage}
            alt="Our Mission"
            sx={{
              display: "block",
              width: isMobile ? "100%" : "60%",
              height: "auto",
              order: isMobile ? 2 : 1,
            }}
          />

          {/* "Our MISSION" Text */}
          <Typography
            variant="h2"
            sx={{
              position: isMobile ? "relative" : "absolute",
              top: isMobile ? 0 : 16,
              right: isMobile ? 0 : "40%",
              fontFamily: '"Helvetica Neue", Arial, sans-serif',
              fontSize: isMobile ? "2.2rem" : isTablet ? "3.5rem" : "5rem",
              fontWeight: 400,
              letterSpacing: "0.1em",
              color: "#000",
              zIndex: 2,
              textAlign: isMobile ? "center" : "left",
              mt: isMobile ? 4 : 0,
              mb: isMobile ? 2 : 0,
              order: isMobile ? 1 : 2,
            }}
          >
            Our
            <br />
            MISSION
          </Typography>

          {/* Mission Statements */}
          <Box
            sx={{
              position: isMobile ? "relative" : "absolute",
              bottom: isMobile ? 0 : 40,
              right: isMobile ? 10 : 20,
              zIndex: 2,
              width: isMobile ? "100%" : "35%",
              textAlign: isMobile ? "center" : "left",
              mt: isMobile ? 4 : 0,
              order: 3,
              px: isMobile ? 0 : 0,
            }}
          >
            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: isMobile ? "2rem" : isTablet ? "3rem" : "4rem",
                  fontWeight: 300,
                  color: "#000",
                  mb: 1,
                }}
              >
                Rethink
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "28px",
                  color: "#000",
                }}
              >
                We think of fashion as a long-term investment.
              </Typography>
            </Box>

            <Box sx={{ mb: isMobile ? 3 : 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: isMobile ? "2rem" : isTablet ? "3rem" : "4rem",
                  fontWeight: 300,
                  color: "#000",
                  mb: 1,
                }}
              >
                Reduce
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "28px",
                  color: "#000",
                }}
              >
                We focus on timeless classics instead of short-lived trends.
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontSize: isMobile ? "2rem" : isTablet ? "3rem" : "4rem",
                  fontWeight: 300,
                  color: "#000",
                  mb: 1,
                }}
              >
                Recycle
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? "1rem" : isTablet ? "1.2rem" : "28px",
                  color: "#000",
                }}
              >
                Every order is shipped in packaging crafted from recycled
                materials.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Resources />
      </Box>
    </Box>
  );
};

export default Sustainability;
